import React, { useState, useEffect, useMemo } from 'react';
import { ListGroup, Spinner } from 'react-bootstrap';
import { FaComments, FaChevronRight } from 'react-icons/fa';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import {
  canAccessChatRoom,
  subscribeAccessibleChatRooms,
} from '../../services/chatService';
import {
  getRoomDisplayName,
  groupChatRooms,
} from '../../services/chatListGroups';
import { effectiveRole } from '../../platform/membershipPermissions';
import { useChatRoomUnreadIds } from '../../platform/lastRead/useChatRoomUnreadIds';
import './UnitSelector.css';

function RoomListItem({ item, hasUnread, onSelect }) {
  const name = getRoomDisplayName(item);

  return (
    <ListGroup.Item
      onClick={() => onSelect(item)}
      className={[
        'unit-list-item',
        'platform-content-list-item',
        hasUnread ? 'unit-list-item--unread platform-content-list-item--unread' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="unit-list-item-content">
        <div className="unit-list-item-icon" aria-hidden="true">
          {hasUnread && (
            <span className="platform-content-list-item-unread-badge">
              <span
                className="platform-unread-dot"
                title="Neprebrana sporočila"
                aria-label="Neprebrana sporočila"
              />
            </span>
          )}
          <FaComments />
        </div>

        <div className="unit-list-item-text">
          <div className="unit-list-item-title">{name}</div>
          <div className="unit-list-item-meta">
            {hasUnread ? 'Nova sporočila' : 'Odpri pogovor'}
          </div>
        </div>

        <div className="unit-list-item-aside">
          <FaChevronRight className="unit-list-item-chevron" aria-hidden="true" />
        </div>
      </div>
    </ListGroup.Item>
  );
}

function UnitSelector({ onSelectUnit }) {
  const { user } = useUser();
  const { tenantId, config } = useTenant();
  const { memberships } = useMembership();
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const unreadRoomIds = useChatRoomUnreadIds(rooms);
  const unreadFlags = useMemo(() => {
    const flags = {};
    unreadRoomIds.forEach((roomId) => {
      flags[roomId] = 1;
    });
    return flags;
  }, [unreadRoomIds]);

  useEffect(() => {
    if (!user || !tenantId) return undefined;

    setIsLoading(true);
    const unsubscribe = subscribeAccessibleChatRooms({
      tenantId,
      memberships,
      legacyUser: user,
      onData: (loadedRooms) => {
        setRooms(loadedRooms);
        setIsLoading(false);
      },
      onError: (error) => {
        console.error('Error subscribing to chat rooms:', error);
        setIsLoading(false);
      },
    });

    return unsubscribe;
  }, [user, tenantId, memberships]);

  const groupedRooms = useMemo(
    () => groupChatRooms(rooms, config, unreadFlags),
    [rooms, config, unreadFlags],
  );

  const handleItemClick = (item) => {
    const role = effectiveRole(memberships, tenantId, user);
    const allowed = canAccessChatRoom({
      room: item,
      role,
      userUid: user.uid,
      userUnit: user.unit,
    });

    if (!allowed) {
      alert('Nimate dostopa do te klepetalnice.');
      return;
    }
    onSelectUnit(item);
  };

  if (isLoading) {
    return (
      <div className="unit-list-loading">
        <Spinner animation="border" size="sm">
          <span className="visually-hidden">Nalaganje...</span>
        </Spinner>
      </div>
    );
  }

  if (groupedRooms.length === 0) {
    return <div className="unit-list-empty">Ni razpoložljivih klepetalnic.</div>;
  }

  return (
    <div className="unit-list-groups">
      {groupedRooms.map(({ group, rooms: groupRooms }) => (
        <section key={group.id} className="unit-list-group">
          <h2 className="platform-section-title">{group.label}</h2>
          <ListGroup className="unit-list platform-content-list">
            {groupRooms.map((item) => (
              <RoomListItem
                key={item.id}
                item={item}
                hasUnread={unreadRoomIds.includes(item.id)}
                onSelect={handleItemClick}
              />
            ))}
          </ListGroup>
        </section>
      ))}
    </div>
  );
}

export default UnitSelector;
